import { useCallback, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useApi } from "../../hooks/useApi";
import { StatusChartResponse, StatusStackedBarChart, useAlertSourceStatusCharts } from "./alertStatusChart";
import { ResolutionsLineChart } from "./ResolutionsLineChart";

interface SubscriptionResolutionsMetricsProps {
  startDate: string;
  endDate: string;
  companyId: number | null;
}

const AUTO_LABELS = ["loop_cancelled", "loop_already_cancelled", "loop_not_subscription", "kaching_cancelled", "kaching_already_cancelled", "kaching_not_subscription", "coc_subscription_cancelled", "coc_subscription_already_cancelled", "coc_not_subscription", "crm_subscription_cancelled", "crm_subscription_already_cancelled", "crm_not_subscription"];
const MANUAL_LABELS = ["subscription_cancelled", "subscription_not_subscription", "subscription_not_found"];

export default function SubscriptionResolutionsMetrics(props: SubscriptionResolutionsMetricsProps) {
  const { data, loading } = useAlertSourceStatusCharts("subscription_resolutions", ["ethoca", "cdrn", "rdr"], props);
  const { data: autoToManualData, loading: autoToManualLoading } = useAlertSourceStatusCharts("subscription-auto-to-manual", ["ethoca", "cdrn", "rdr"], props);
  const { data: chargebackData, loading: chargebackLoading } = useChargebackCancellationsChart(props);
  const ethocaData = data.ethoca ?? null;
  const cdrnData = data.cdrn ?? null;
  const rdrData = data.rdr ?? null;
  const ethocaAutoToManualData = autoToManualData.ethoca ?? null;
  const cdrnAutoToManualData = autoToManualData.cdrn ?? null;
  const rdrAutoToManualData = autoToManualData.rdr ?? null;

  const hasAnyData =
    (ethocaData && ethocaData.summary.total > 0) ||
    (cdrnData && cdrnData.summary.total > 0) ||
    (rdrData && rdrData.summary.total > 0) ||
    (ethocaAutoToManualData && ethocaAutoToManualData.summary.total > 0) ||
    (cdrnAutoToManualData && cdrnAutoToManualData.summary.total > 0) ||
    (rdrAutoToManualData && rdrAutoToManualData.summary.total > 0) ||
    (chargebackData && chargebackData.summary.total > 0);
  if (!loading && !autoToManualLoading && !chargebackLoading && !hasAnyData) {
    return null;
  }

  const rowSx = { display: "flex", gap: 3, flexWrap: { xs: "wrap", md: "nowrap" }, alignItems: "flex-start" } as const;

  return (
    <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={rowSx}>
        <ResolutionsLineChart
          title="Ethoca Subscription Resolutions (Manual vs Auto)"
          data={ethocaData}
          loading={loading}
          autoLabels={AUTO_LABELS}
          manualLabels={MANUAL_LABELS}
          autoSeriesLabel="Auto-Cancelled"
          manualSeriesLabel="Manual Resolved"
        />
        <StatusStackedBarChart
          title="Ethoca Subscriptions Auto → Manual"
          data={ethocaAutoToManualData}
          loading={autoToManualLoading}
        />
      </Box>
      <Box sx={rowSx}>
        <ResolutionsLineChart
          title="CDRN Subscription Resolutions (Manual vs Auto)"
          data={cdrnData}
          loading={loading}
          autoLabels={AUTO_LABELS}
          manualLabels={MANUAL_LABELS}
          autoSeriesLabel="Auto-Cancelled"
          manualSeriesLabel="Manual Resolved"
        />
        <StatusStackedBarChart
          title="CDRN Subscriptions Auto → Manual"
          data={cdrnAutoToManualData}
          loading={autoToManualLoading}
        />
      </Box>
      <Box sx={rowSx}>
        <ResolutionsLineChart
          title="RDR Subscription Resolutions (Manual vs Auto)"
          data={rdrData}
          loading={loading}
          autoLabels={AUTO_LABELS}
          manualLabels={MANUAL_LABELS}
          autoSeriesLabel="Auto-Cancelled"
          manualSeriesLabel="Manual Resolved"
        />
        <StatusStackedBarChart
          title="RDR Subscriptions Auto → Manual"
          data={rdrAutoToManualData}
          loading={autoToManualLoading}
        />
      </Box>
      <Box sx={rowSx}>
        <StatusStackedBarChart
          title="Chargeback Subscription Cancellations"
          data={chargebackData}
          loading={chargebackLoading}
        />
        <Box sx={{ flex: 1 }} />
      </Box>
    </Box>
  );
}

function useChargebackCancellationsChart(params: { startDate: string; endDate: string; companyId: number | null }) {
  const { get, loading } = useApi();
  const [data, setData] = useState<StatusChartResponse | null>(null);
  const { startDate, endDate, companyId } = params;

  const fetchChartData = useCallback(async () => {
    if (!startDate || !endDate) return;
    const qs = new URLSearchParams({
      startDate,
      endDate,
      ...(companyId && { companyId: companyId.toString() }),
    }).toString();
    try {
      const res = await get<StatusChartResponse>(`/api/dispute-history/chargeback-cancellations/chart?${qs}`);
      setData(res);
    } catch (err) {
      console.error("Failed to fetch chargeback cancellations chart:", err);
      setData(null);
    }
  }, [startDate, endDate, companyId, get]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  return { data, loading };
}
